import * as THREE from "three";
import { tokens } from "../styles/tokens";

/**
 * Fresnel-alpha glow shell (ADR 202) — the highlight look for found/inspected
 * loads. Alpha peaks at the silhouette (grazing normals) and fades on faces
 * looking at the camera, so a box shell reads as a soft energy outline instead
 * of a solid glass slab. Additive blending over the dark navy scene makes it
 * glow without post-processing — the whole effect is one material and (for the
 * search matches) one instanced draw, per ADR 199's budget.
 *
 * The vertex shader carries the `USE_INSTANCING` branch: three.js defines it
 * automatically when the material renders on an InstancedMesh, so the same
 * material works on the instanced search auras and the single inspected mesh.
 */

const VERT = /* glsl */ `
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  void main() {
    vec4 mPos = vec4(position, 1.0);
    vec3 n = normal;
    #ifdef USE_INSTANCING
      mPos = instanceMatrix * mPos;
      n = mat3(instanceMatrix) * n;
    #endif
    vec4 wPos = modelMatrix * mPos;
    vWorldPos = wPos.xyz;
    vWorldNormal = mat3(modelMatrix) * n;
    gl_Position = projectionMatrix * viewMatrix * wPos;
  }
`;

const FRAG = /* glsl */ `
  uniform vec3 uColor;
  uniform float uIntensity;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  void main() {
    vec3 dv = cameraPosition - vWorldPos;
    float dist = length(dv);
    vec3 v = dv / dist;
    float rim = 1.0 - abs(dot(normalize(vWorldNormal), v));
    // Distance-adaptive fill (tuned live on JSL): up close the glow lives on
    // the silhouette — an aura, not a glass box. From across the warehouse a
    // load is a handful of pixels and the rim alone vanishes, so the faces
    // fill in and the match still beacons.
    float base = mix(0.06, 0.38, smoothstep(12.0, 50.0, dist));
    float a = (base + (1.0 - base) * pow(rim, 2.2)) * uIntensity;
    gl_FragColor = vec4(uColor, a);
  }
`;

export function makeAuraMaterial(color: string): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Color(color) }, uIntensity: { value: 1 } },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
}

/** Search matches — CTA blue, pulsed from PalletLoadLayer's frame loop. */
export const searchAuraMaterial = makeAuraMaterial(tokens.blue[500]);

/** Inspected bin — near-white and steady, so "open in the inspector" never
 *  reads as another search match. */
export const selectedAuraMaterial = makeAuraMaterial(tokens.blue[50]);

/** Hovered load (ADR 279) — the selected shell at a fraction of the volume:
 *  enough to say "clickable", never mistakable for selected or matched. */
export const hoverAuraMaterial = makeAuraMaterial(tokens.blue[300]);
hoverAuraMaterial.uniforms.uIntensity.value = 0.45;
